import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Plus, GripVertical } from 'lucide-react';
import { SortableList } from './SortableList';
import { ItemActions } from './ItemActions';
import { PreviewModal } from './PreviewModal';
import { RichTextEditor } from './RichTextEditor';

const CareersManager = () => {
  const [careers, setCareers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchCareers = async () => {
    const res = await adminApi.get('/careers');
    setCareers(res.data || []);
  };
  useEffect(() => { fetchCareers(); }, []);

  const blank = () => ({
    title: '',
    department: '',
    location: '',
    type: 'Full-Time',
    description: '',
    requirements: [],
    responsibilities: [],
    order: careers.length,
    published: true,
  });

  const linesToList = (text) => text.split('\n').map((s) => s.trim()).filter(Boolean);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing.id && careers.find((x) => x.id === editing.id)) {
        await adminApi.put(`/careers/${editing.id}`, editing);
      } else {
        await adminApi.post('/careers', { ...editing, id: undefined });
      }
      toast({ title: 'Saved' });
      setEditing(null);
      fetchCareers();
    } catch (err) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this career posting?')) return;
    await adminApi.delete(`/careers/${id}`);
    fetchCareers();
  };

  const handleReorder = async (reordered) => {
    setCareers(reordered);
    try { await adminApi.put('/careers/reorder', { ids: reordered.map((r) => r.id) }); } catch {}
  };

  return (
    <div className="space-y-6" data-testid="careers-manager">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Careers</h2>
        <Button onClick={() => setEditing(blank())} className="bg-[#1565C0]" data-testid="add-career-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Opening
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader><CardTitle>{editing.id ? 'Edit Career' : 'New Career'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label>Job Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} required data-testid="career-title-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Department</Label>
                  <Input value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })} />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} placeholder="Kuala Lumpur" />
                </div>
                <div>
                  <Label>Type</Label>
                  <Input value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} placeholder="Full-Time" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <RichTextEditor value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
              </div>
              <div>
                <Label>Requirements (one per line)</Label>
                <Textarea
                  value={(editing.requirements || []).join('\n')}
                  onChange={(e) => setEditing({ ...editing, requirements: linesToList(e.target.value) })}
                  rows={4}
                />
              </div>
              <div>
                <Label>Responsibilities (one per line)</Label>
                <Textarea
                  value={(editing.responsibilities || []).join('\n')}
                  onChange={(e) => setEditing({ ...editing, responsibilities: linesToList(e.target.value) })}
                  rows={4}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.published !== false} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
                <Label>Published</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-[#1565C0]" data-testid="save-career-btn">{loading ? 'Saving…' : 'Save'}</Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <SortableList
        items={careers}
        onReorder={handleReorder}
        renderItem={(c) => (
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{c.title}</h3>
                <p className="text-sm text-gray-600">{c.department} • {c.location} • {c.type}</p>
              </div>
              <ItemActions
                collection="careers"
                item={c}
                onChanged={fetchCareers}
                onEdit={setEditing}
                onDelete={handleDelete}
                onPreview={setPreviewing}
              />
            </CardContent>
          </Card>
        )}
      />
      <PreviewModal kind="career" item={previewing} onClose={() => setPreviewing(null)} />
      {careers.length === 0 && !editing && <p className="text-center text-gray-500 py-8">No career postings yet.</p>}
    </div>
  );
};

export default CareersManager;
